import { useEffect, useState, useCallback } from 'react';
import { Copy, Check, Code2, FileCode, Save, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { formatSql } from '../../common/utils/sqlFormatter';
import type { DatabaseEngineId } from '@/core/engines';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { writeClipboardText } from '@/lib/clipboard';

type QueryPreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  engineId?: DatabaseEngineId;
  onOpenInQueryEditor?: (query: string) => void;
  tableName?: string;
  title?: string;
  description?: string;
  testIdPrefix?: string;
  onExecute?: () => Promise<{ success: boolean; error?: string }>;
  executeLabel?: string;
  executingLabel?: string;
  executeDisabled?: boolean;
};

/**
 * Dialog component that displays the generated SQL query for the current
 * Table Explorer state (filters, sorting, columns, limit).
 */
export function QueryPreviewDialog({
  open,
  onOpenChange,
  query,
  engineId,
  onOpenInQueryEditor,
  tableName,
  title,
  description,
  testIdPrefix = 'query-preview',
  onExecute,
  executeLabel = 'Execute',
  executingLabel = 'Executing...',
  executeDisabled = false,
}: QueryPreviewDialogProps) {
  const [copied, setCopied] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executeError, setExecuteError] = useState<string | null>(null);

  useEffect(() => {
    if (open) return;
    setExecuteError(null);
    setIsExecuting(false);
  }, [open]);

  const handleExecuteClick = useCallback(async () => {
    if (!onExecute) return;
    setIsExecuting(true);
    setExecuteError(null);
    try {
      const result = await onExecute();
      if (result.success) {
        onOpenChange(false);
      } else {
        setExecuteError(result.error || 'Failed to execute changes.');
      }
    } catch (err) {
      setExecuteError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsExecuting(false);
    }
  }, [onExecute, onOpenChange]);

  // Format the query for display. `formatSql` is async because `sql-formatter`
  // is code-split; until it resolves we show the unformatted query rather than
  // an empty pane, so the dialog never flashes blank.
  const [formattedQuery, setFormattedQuery] = useState(query);
  useEffect(() => {
    // Don't format loading/error messages
    if (query.startsWith('--')) {
      setFormattedQuery(query);
      return;
    }
    let cancelled = false;
    setFormattedQuery(query);
    void formatSql(query, engineId).then((formatted) => {
      if (!cancelled) setFormattedQuery(formatted);
    });
    return () => {
      cancelled = true;
    };
  }, [query, engineId]);

  const handleCopy = useCallback(async () => {
    const ok = await writeClipboardText(formattedQuery);
    if (!ok) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = formattedQuery;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [formattedQuery]);

  const dialogTitle = title || 'Query Preview';
  const dialogDescription = description || (tableName ? `Generated SQL query for ${tableName}` : undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={`${testIdPrefix}-dialog`}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-4 top-4 h-7 px-2"
          onClick={() => onOpenChange(false)}
          data-testid={`${testIdPrefix}-close-button`}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code2 className="h-5 w-5" />
            {dialogTitle}
          </DialogTitle>
          {dialogDescription && (
            <DialogDescription>
              {dialogDescription}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="relative">
          <pre
            className="bg-muted/50 border border-border rounded-md p-4 text-sm font-mono overflow-x-auto whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto"
            data-testid={`${testIdPrefix}-content`}
          >
            {formattedQuery}
          </pre>

          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="absolute top-2 right-2 h-7 px-2"
            data-testid={`${testIdPrefix}-copy-button`}
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 mr-1 text-green-500" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5 mr-1" />
                Copy
              </>
            )}
          </Button>
        </div>

        {executeError && (
          <div
            className="p-3 rounded-md bg-destructive/10 text-destructive text-sm"
            data-testid={`${testIdPrefix}-execute-error`}
          >
            {executeError}
          </div>
        )}

        {(onOpenInQueryEditor || onExecute) && (
          <DialogFooter>
            {onOpenInQueryEditor && (
              <Button
                type="button"
                variant={onExecute ? 'outline' : 'default'}
                onClick={() => {
                  onOpenInQueryEditor(formattedQuery);
                  onOpenChange(false);
                }}
                disabled={isExecuting}
                data-testid={`${testIdPrefix}-open-in-editor-button`}
              >
                <FileCode className="h-4 w-4 mr-2" />
                Open in Query Editor
              </Button>
            )}

            {onExecute && (
              <Button
                type="button"
                onClick={handleExecuteClick}
                disabled={isExecuting || executeDisabled}
                data-testid={`${testIdPrefix}-execute-button`}
              >
                {isExecuting ? (
                  <>
                    <RandomDotmSpinner size={16} className="mr-2" />
                    {executingLabel}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {executeLabel}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
